import { StaleSessionNotice } from '@brainfocus/web/components/stale_session_notice/stale_session_notice';
import { UploadSnackbar } from '@brainfocus/web/components/upload_snackbar/upload_snackbar';
import { VersionFooter } from '@brainfocus/web/components/version_footer/version_footer';
import { Router, RouterRoute } from '@brainfocus/web/router/router';
import type { LoaderFunction } from '@brainfocus/web/router/loader_manager';
import { allNeuroimageFiles, BrainFocusApi } from '@brainfocus/web/brainfocus_api';
import { lazyComponent, type LazyComponent } from '@brainfocus/web/shared/platform/lazy_component';
import { NavigationStateProvider } from '@brainfocus/web/router/navigation_state';
import { LoaderProvider, useLoaderManager } from '@brainfocus/web/router/loader_context';
import { useNavigate } from '@brainfocus/web/router/use_navigate';
import { UploadManager } from '@brainfocus/web/utils/upload_manager';
import { Error404 } from '@brainfocus/web/routes/error404';
import { Login } from '@brainfocus/web/routes/login/login';
import { ResetPassword } from '@brainfocus/web/routes/reset_password/reset_password';
import { ResetPasswordNew } from '@brainfocus/web/routes/reset_password_new/reset_password_new';
import { AcceptTerms } from '@brainfocus/web/routes/accept_terms/accept_terms';
import { Signup } from '@brainfocus/web/routes/signup/signup';
import { flash } from '@brainfocus/web/flash';
import { IS_ADMIN, IS_ANON, IS_LOGGED_IN, IS_TRIAL_SIGNUP_ENABLED } from '@brainfocus/web/services/user';
import { getTrialSignupDisabledMessage, isTrialSignupEnabled } from '@brainfocus/web/services/trial_signup';
import { redirectToAcceptanceIfNeeded } from '@brainfocus/web/services/acceptance';
import { redirectToNdaPendingIfNeeded } from '@brainfocus/web/services/nda';
import { wireFullscreenToggle } from '@brainfocus/web/services/fullscreen_toggle';
import { usePathname } from '@brainfocus/web/router/use_pathname';
import { useEventCallback } from '@brainfocus/web/utils/react/use_event_callback';
import { useEffect } from 'preact/hooks';

// Route pages load on demand (#1169); the anonymous entry pages above stay in the main script.
const Manage = lazyComponent(() => import(/* webpackChunkName: "page-manage" */ '@brainfocus/web/components/manage/manage').then(m => m.Manage));
const PatientRecordingsView = lazyComponent(() => import(/* webpackChunkName: "page-manage" */ '@brainfocus/web/components/manage/patientRecordingsView/patientRecordingsView').then(m => m.PatientRecordingsView));
const RecordingDetailView = lazyComponent(() => import(/* webpackChunkName: "page-manage" */ '@brainfocus/web/components/manage/recordingDetailView/recordingDetailView').then(m => m.RecordingDetailView));
const AnalysisDetailView = lazyComponent(() => import(/* webpackChunkName: "page-manage" */ '@brainfocus/web/components/manage/analysisDetailView/analysisDetailView').then(m => m.AnalysisDetailView));
const AnalysisPreview = lazyComponent(() => import(/* webpackChunkName: "page-manage" */ '@brainfocus/web/components/manage/managePatient/analysisPreview/analysisPreview').then(m => m.AnalysisPreview));
const AnalysisView = lazyComponent(() => import(/* webpackChunkName: "page-analysis" */ '@brainfocus/web/components/analysis_view/analysis_view').then(m => m.AnalysisView));
const EdfPreviewView = lazyComponent(() => import(/* webpackChunkName: "page-edf-preview" */ '@brainfocus/web/components/edf_preview/edf_preview_view').then(m => m.EdfPreviewView));
const NeuroimagePreviewView = lazyComponent(() => import(/* webpackChunkName: "page-neuroimage-preview" */ '@brainfocus/web/features/neuroimaging/neuroimage_preview/neuroimage_preview_view').then(m => m.NeuroimagePreviewView));
const Admin = lazyComponent(() => import(/* webpackChunkName: "page-admin" */ '@brainfocus/web/routes/admin/admin').then(m => m.Admin));
const Home = lazyComponent(() => import(/* webpackChunkName: "page-home" */ '@brainfocus/web/routes/home/home').then(m => m.Home));
const Profile = lazyComponent(() => import(/* webpackChunkName: "page-profile" */ '@brainfocus/web/routes/profile/profile').then(m => m.Profile));

/** A route loader that also loads its pages' code, so an in-app navigation completes with both (#1169). */
export function withPages(pages: Pick<LazyComponent<unknown>, 'preload'>[], loader: LoaderFunction): LoaderFunction {
    return async (context) => {
        const [data] = await Promise.all([loader(context), ...pages.map(page => page.preload())]);
        return data;
    };
}

/** Exported for the route table's tests (#1169). */
export const ROUTES: RouterRoute[] = [
    // Manage nested routes - MUST come BEFORE generic ./manage route
    // IMPORTANT: More specific routes (longer paths) MUST come FIRST
    {
        path: './manage/patient/:patientId/recording/:recordingId/analysis/:analysisId',
        guard: [IS_LOGGED_IN],
        loader: withPages([Manage, AnalysisDetailView], async ({ params }) => {
            const analysisId = parseInt(params['analysisId'] || '0');
            const patientId = parseInt(params['patientId'] || '0');
            const recordingId = parseInt(params['recordingId'] || '0');
            const [analysis, patient, recordings, seizureGroups] = await Promise.all([
                BrainFocusApi.getAnalysis(analysisId),
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getRecordings(patientId),
                BrainFocusApi.getPatientsSeizures(patientId),
            ]);
            const recording = recordings.find(r => r.id === recordingId);
            return { analysis, patient, recording, seizureGroups };
        }),
        factory: (patientId: string, recordingId: string, analysisId: string) => (
            <Manage>
                <AnalysisDetailView
                    analysisId={parseInt(analysisId)}
                    patientId={parseInt(patientId)}
                    recordingId={parseInt(recordingId)}
                />
            </Manage>
        ),
    },
    {
        path: './manage/patient/:patientId/recording/:recordingId',
        guard: [IS_LOGGED_IN],
        loader: withPages([Manage, RecordingDetailView], async ({ params }) => {
            const patientId = parseInt(params['patientId'] || '0');
            const recordingId = parseInt(params['recordingId'] || '0');
            const [patient, recordings, analyses, seizureGroups] = await Promise.all([
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getRecordings(patientId),
                BrainFocusApi.getAnalyses({ patientIds: [patientId], count: -1, uploaderIds: [] }),
                BrainFocusApi.getPatientsSeizures(patientId),
            ]);
            const recording = recordings.find(r => r.id === recordingId);
            return { patient, recording, analyses: analyses.filter(a => a.edfId === recordingId), seizureGroups };
        }),
        factory: (patientId: string, recordingId: string) => (
            <Manage>
                <RecordingDetailView
                    patientId={parseInt(patientId)}
                    recordingId={parseInt(recordingId)}
                />
            </Manage>
        ),
    },
    {
        path: './manage/patient/:patientId/preview/:analysisId',
        guard: [IS_LOGGED_IN],
        loader: withPages([Manage, AnalysisPreview], async ({ params }) => {
            const analysisId = parseInt(params['analysisId'] || '0');
            const patientId = parseInt(params['patientId'] || '0');
            const [analysis, patient, seizureGroups] = await Promise.all([
                BrainFocusApi.getAnalysis(analysisId),
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getPatientsSeizures(patientId),
            ]);
            return { analysis, patient, seizureGroups };
        }),
        factory: (patientId: string, analysisId: string) => (
            <Manage>
                <AnalysisPreview
                    key={analysisId}
                    analysisId={parseInt(analysisId)}
                    patientId={parseInt(patientId)}
                />
            </Manage>
        ),
    },
    {
        path: './manage/patient/:patientId/edf/:edfId/preview',
        guard: [IS_LOGGED_IN],
        loader: withPages([EdfPreviewView], async ({ params }) => {
            const patientId = parseInt(params['patientId'] || '0');
            const edfId = parseInt(params['edfId'] || '0');
            const [patient, recordings, edfInfo] = await Promise.all([
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getRecordings(patientId),
                BrainFocusApi.edfInfo(edfId),
            ]);
            const recording = recordings.find(r => r.id === edfId);
            return { patient, recording, edfInfo };
        }),
        factory: (_patientId: string, edfId: string) => (
            <EdfPreviewView key={edfId} />
        ),
    },
    {
        path: './manage/patient/:patientId/mri/:mriId/preview',
        guard: [IS_LOGGED_IN],
        loader: withPages([NeuroimagePreviewView], async ({ params }) => {
            const patientId = parseInt(params['patientId'] || '0');
            const mriId = parseInt(params['mriId'] || '0');
            const [patient, mris] = await Promise.all([
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getPatientMris(patientId),
            ]);
            const mri = mris.find(m => m.id === mriId);
            return { patient, mriId, mriName: mri?.name };
        }),
        factory: (_patientId: string, mriId: string) => (
            <NeuroimagePreviewView key={mriId} />
        ),
    },
    {
        // Full-page preview of an uploaded NeuroimageFile before coregistration (#620).
        path: './manage/patient/:patientId/neuroimage/:fileId/preview',
        guard: [IS_LOGGED_IN],
        loader: withPages([NeuroimagePreviewView], async ({ params }) => {
            const patientId = parseInt(params['patientId'] || '0');
            const fileId = parseInt(params['fileId'] || '0');
            const [patient, filesResponse] = await Promise.all([
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getNeuroimageFiles(patientId),
            ]);
            const file = allNeuroimageFiles(filesResponse).find(f => f.id === fileId);
            return { patient, neuroimageId: fileId, mriName: file?.filename };
        }),
        factory: (_patientId: string, fileId: string) => (
            <NeuroimagePreviewView key={`nif-${fileId}`} />
        ),
    },
    {
        path: './manage/patient/:patientId',
        guard: [IS_LOGGED_IN],
        loader: withPages([Manage, PatientRecordingsView], async ({ params }) => {
            const patientId = parseInt(params['patientId'] || '0');
            const [patient, recordings] = await Promise.all([
                BrainFocusApi.getPatient(patientId),
                BrainFocusApi.getRecordings(patientId),
            ]);
            return { patient, recordings };
        }),
        factory: (patientId: string) => (
            <Manage>
                <PatientRecordingsView patientId={parseInt(patientId)} />
            </Manage>
        ),
    },
    {
        path: './manage',
        guard: [IS_LOGGED_IN],
        factory: () => <Manage />,
    },
    {
        path: './view-analysis/:analysisId',
        guard: IS_LOGGED_IN,
        loader: withPages([AnalysisView], async ({ params }) => {
            return await BrainFocusApi.getAnalysis(parseInt(params['analysisId'] || '0'));
        }),
        factory: id => <AnalysisView analysisId={parseInt(id)} />,
    },
    {
        path: './admin',
        guard: [IS_LOGGED_IN, IS_ADMIN],
        factory: () => <Admin />,
    },
    {
        path: './profile',
        guard: IS_LOGGED_IN,
        factory: () => <Profile />,
    },
    {
        path: './login',
        guard: IS_ANON,
        factory: () => <Login />,
    },
    {
        path: './reset-password$',
        guard: IS_ANON,
        factory: () => <ResetPassword />,
    },
    {
        path: './reset-password/new$',
        guard: IS_ANON,
        factory: () => <ResetPasswordNew />,
    },
    {
        path: './trial-signup$',
        guard: [IS_ANON, IS_TRIAL_SIGNUP_ENABLED],
        onGuardRedirect: () => {
            // Only show message if trial signup is disabled (not if user is just logged in)
            if (!isTrialSignupEnabled()) {
                flash(getTrialSignupDisabledMessage());
            }
        },
        factory: () => <Signup />,
    },
    {
        path: './accept-terms$',
        guard: IS_LOGGED_IN,
        factory: () => <AcceptTerms />,
    },
    {
        path: './$',
        guard: IS_LOGGED_IN,
        factory: () => <Home />,
    },
    {
        path: '', // catch all non-resolved routes
        factory: () => <Error404 />,
    },
];

/**
 * Inner App component that has access to navigation state
 */
function AppInner() {
    const navigate = useNavigate();
    const loaderManager = useLoaderManager();
    const pathname = usePathname();

    // ToS/Privacy gate (#756): on every client-side route change, proactively
    // return a blocked user to /accept-terms. This covers destinations that
    // fire no gate-checked API call on arrival (e.g. the Admin landing view),
    // which the execFetch 403 handler alone would miss.
    useEffect(() => {
        redirectToAcceptanceIfNeeded();
    }, [pathname]);

    // NDA gate (#529 / #795 R4): same proactive recovery for pending
    // industry-player users, so client-side navigation to a gated view returns
    // them to /nda-pending instead of a raw error banner.
    useEffect(() => {
        redirectToNdaPendingIfNeeded();
    }, [pathname]);

    // Make all non-preact links with routerLink attribute use our router's
    // navigate.
    const navigateFromLink = useEventCallback((href: string) => navigate(href));
    useEffect(() => {
        document.querySelectorAll('[routerLink]').forEach((element) => {
            element.addEventListener('click', (e) => {
                void navigateFromLink((e.target as HTMLAnchorElement).getAttribute('href')!);
                e.preventDefault();
            });
        });
    }, [navigateFromLink]);

    // Global-header fullscreen toggle (#967): same non-Preact-template pattern
    // as the routerLink wiring above — top.html is a plain Jinja partial.
    useEffect(() => wireFullscreenToggle(), []);

    // Global upload completion listener - invalidates cache when uploads finish
    // This ensures data is refreshed regardless of which route the user is on
    useEffect(() => {
        const uploadManager = UploadManager.getInstance();
        const trackedUploads = new Map<string, number>(); // uploadId -> patientId

        const subscription = uploadManager.activeUploads$.subscribe((uploads) => {
            // Detect uploads that disappeared (completed or removed)
            trackedUploads.forEach((_patientId, uploadId) => {
                if (!uploads.has(uploadId)) {
                    // Upload was removed (completed) - invalidate cache
                    loaderManager.invalidate('./manage/patient/:patientId');
                    trackedUploads.delete(uploadId);
                }
            });

            // Track new uploads
            uploads.forEach((upload, uploadId) => {
                if (!trackedUploads.has(uploadId) && upload.patientId) {
                    trackedUploads.set(uploadId, upload.patientId);
                }
            });
        });

        return () => subscription.unsubscribe();
    }, [loaderManager]);

    return (
        <>
            <Router routes={ROUTES} />
            <UploadSnackbar />
            <VersionFooter />
        </>
    );
}

/**
 * Index component of the preact application. Hosts the router where routes put
 * their content.
 */
export function App() {
    return (
        <LoaderProvider>
            <NavigationStateProvider>
                <AppInner />
                {/*
                  * Outside AppInner so it survives any route change, and last so it
                  * paints over the app (#871). Renders nothing until a different
                  * user signs in in another tab.
                  */}
                <StaleSessionNotice />
            </NavigationStateProvider>
        </LoaderProvider>
    );
}
